import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function SEO({
    title,
    description,
    keywords,
    name,
    type,
    image,
    url,
    schemaType // 'organization', 'course', 'service', or 'localBusiness'
}) {
    const siteTitle = "PthinkS - Qt & QML Software Development and Training";
    const defaultDescription = "PthinkS provides expert Qt & QML software development, consulting, and training services in Bengaluru, India. Over 100 skilled Qt developers for cross-platform and embedded systems solutions.";
    const defaultKeywords = "Qt, QML, Qt Training, QML Training, Qt Consulting, Qt Software Development, Qt QML India, Qt Bengaluru, Cross-platform Development, Embedded Systems, C++ Qt";
    const defaultImage = "https://pthinks.com/logo512.png";
    const siteUrl = "https://pthinks.com";
    const canonicalUrl = url ? `${siteUrl}${url}` : siteUrl;

    // Organization Schema (appears on all pages)
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "PthinkS Pvt Ltd",
        "alternateName": "PthinkS",
        "url": "https://pthinks.com",
        "logo": "https://pthinks.com/logo512.png",
        "description": "Premier Qt and QML software development company with over 100 skilled developers in India",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Bengaluru",
            "addressRegion": "Karnataka",
            "addressCountry": "IN"
        },
        "sameAs": [],
        "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": ["English", "Hindi"]
        },
        "knowsAbout": ["Qt Framework", "QML", "C++", "Cross-platform Development", "Embedded Systems", "OpenGL"]
    };

    // Local Business Schema
    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "PthinkS Pvt Ltd",
        "image": "https://pthinks.com/logo512.png",
        "url": "https://pthinks.com",
        "telephone": "",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Bengaluru",
            "addressRegion": "Karnataka",
            "addressCountry": "IN"
        },
        "priceRange": "$$",
        "description": "Expert Qt and QML software development, consulting, and training services"
    };

    // Course Schema (for training pages)
    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": title || "Qt & QML Training",
        "description": description || defaultDescription,
        "provider": {
            "@type": "Organization",
            "name": "PthinkS Pvt Ltd",
            "sameAs": "https://pthinks.com"
        },
        "educationalLevel": "Professional",
        "teaches": ["Qt Framework", "QML", "C++", "Cross-platform Development"],
        "courseMode": ["online", "onsite"],
        "availableLanguage": ["English"],
        "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "Blended",
            "location": {
                "@type": "Place",
                "name": "Bengaluru, India"
            }
        }
    };

    // Service Schema (for consulting/service pages)
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": title || "Qt & QML Services",
        "description": description || defaultDescription,
        "provider": {
            "@type": "Organization",
            "name": "PthinkS Pvt Ltd",
            "url": "https://pthinks.com"
        },
        "areaServed": {
            "@type": "Country",
            "name": "India"
        },
        "serviceType": "Software Development and Consulting"
    };

    // Select appropriate schema based on page type
    const getSchema = () => {
        switch (schemaType) {
            case 'course':
                return courseSchema;
            case 'service':
                return serviceSchema;
            case 'localBusiness':
                return localBusinessSchema;
            default:
                return organizationSchema;
        }
    };

    return (
        <Helmet>
            {/* Standard metadata tags */}
            <html lang="en" />
            <title>{title ? `${title} | PthinkS - Qt & QML Experts` : siteTitle}</title>
            <meta name='description' content={description || defaultDescription} />
            <meta name='keywords' content={keywords || defaultKeywords} />

            {/* Canonical URL */}
            <link rel="canonical" href={canonicalUrl} />

            {/* Additional SEO meta tags */}
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <meta name="author" content="PthinkS Pvt Ltd" />
            <meta name="geo.region" content="IN-KA" />
            <meta name="geo.placename" content="Bengaluru" />
            <meta name="language" content="English" />

            {/* Open Graph tags */}
            <meta property="og:type" content={type || 'website'} />
            <meta property="og:title" content={title ? `${title} | PthinkS` : siteTitle} />
            <meta property="og:description" content={description || defaultDescription} />
            <meta property="og:image" content={image || defaultImage} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content="PthinkS - Qt & QML Experts" />
            <meta property="og:locale" content="en_IN" />

            {/* Twitter tags */}
            <meta name="twitter:creator" content={name || '@PthinkS'} />
            <meta name="twitter:card" content={type === 'article' ? 'summary_large_image' : 'summary'} />
            <meta name="twitter:title" content={title ? `${title} | PthinkS` : siteTitle} />
            <meta name="twitter:description" content={description || defaultDescription} />
            <meta name="twitter:image" content={image || defaultImage} />

            {/* JSON-LD Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(getSchema())}
            </script>
        </Helmet>
    );
}
